import { useMutation } from '@apollo/client'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import WarningIcon from '@mui/icons-material/Warning'
import XIcon from '@mui/icons-material/X'
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Stack,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material'
import { TwitterAccount, FacebookPage } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { DISCONNECT_TWITTER_ACCOUNT } from '@src/components/brands/ManageBrand'
import { FacebookPermissionsDialog } from '@src/components/shared/FacebookPermissions/FacebookPermissionsDialog'
import AppConfig from '@src/config/AppConfig'
import ConfirmDialog from '@src/lib/ConfirmDialog'
import { useFacebook } from '@src/lib/useFacebook'
import { useRetailerAccountSocialAccounts } from '@src/lib/useRetailerAccountSocialAccounts'
import {
  userSelector,
  currentRetailerAccountSelector,
} from '@src/stores/app/selectors'
// pbxx2 uses the omniauth-twitter gem for Twitter authentication, which doesnt seem to support returning the oauth_token directly and only redirects
// I couldn't get this behavior to play nice with graphql, so I'm just going to use the same window behavior for now
interface createTwitterAuthOptions {
  authOriginUrl: string
  userId: string
  retailerAccountId: string
  forceLogin?: boolean
  omniauthWindowType?: string
}

interface SocialAccount {
  id: string
  name: string
  image: string
  channels: string[]
  authenticated: boolean
  account: TwitterAccount | FacebookPage
}

const createTwitterAuthUrl = ({
  authOriginUrl,
  userId,
  retailerAccountId,
  forceLogin = true,
  omniauthWindowType = 'sameWindow',
}: createTwitterAuthOptions) => {
  // Base URL for the OAuth flow
  const baseUrl = `${AppConfig.API_BASE}/omniauth/twitter`
  const encodedAuthOriginUrl = encodeURIComponent(authOriginUrl)

  // Construct the query string
  const queryParams = `auth_origin_url=${encodedAuthOriginUrl}&force_login=${forceLogin}&user_id=${userId}&retailer_account_id=${retailerAccountId}&omniauth_window_type=${omniauthWindowType}&resource_class=User`
  return `${baseUrl}?${queryParams}`
}

const RetailerSettingsSocialAccounts: React.FC = () => {
  return (
    <Stack direction="column" spacing={4} sx={{ marginTop: 5 }}>
      <Typography variant="body1" gutterBottom>
        {I18n.t('retailerSettings.socialAccounts.subtitle')}
      </Typography>
      <Divider />
      <FacebookSocialAccountTable />
      <TwitterSocialAccountTable />
    </Stack>
  )
}

export default RetailerSettingsSocialAccounts

const FacebookSocialAccountTable: React.FC<{}> = () => {
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const currentUser = useSelector(userSelector)
  const { enqueueSnackbar } = useSnackbar()
  const { facebookPages, loading, refetch } = useRetailerAccountSocialAccounts()
  const [currentSocialAccountId, setCurrentSocialAccountId] = useState<
    string | null
  >(null)
  // const [enqueueSnackbar] = useSnackbar()
  const [isFacebookPermissionsDialogOpen, setIsFacebookPermissionsDialogOpen] =
    useState(false)
  const { deleteFacebookPage } = useFacebook(currentUser, retailerAccount)
  return (
    <>
      <SocialAccountTable
        onAdd={() => {
          setIsFacebookPermissionsDialogOpen(true)
          setCurrentSocialAccountId(null)
        }}
        onEdit={(page) => {
          setIsFacebookPermissionsDialogOpen(true)
          setCurrentSocialAccountId(page.id)
        }}
        onRemove={(page) => {
          deleteFacebookPage(page.id)
            .then(() => {
              enqueueSnackbar(
                I18n.t(
                  'retailerSettings.socialAccounts.confirmationDialog.success',
                ),
                {
                  variant: 'success',
                },
              )
              refetch()
            })
            .catch((error) => {
              enqueueSnackbar(
                I18n.t(
                  'retailerSettings.socialAccounts.confirmationDialog.error',
                ),
                {
                  variant: 'error',
                },
              )
            })
        }}
        content={{
          title: I18n.t('retailerSettings.socialAccounts.facebookAccounts'),
          removeAccountContent: I18n.t(
            'retailerSettings.socialAccounts.confirmationDialog.description',
          ),
          removeAccountTitle: I18n.t(
            'retailerSettings.socialAccounts.confirmationDialog.title',
          ),
        }}
        loading={loading}
        rows={facebookPages.map((page) => {
          const channels = ['facebook']
          if (
            page.instagram_business_account_id &&
            page.remote_token_status?.is_valid
          ) {
            channels.push('instagram')
          } else {
            channels.push('instagram-invalid')
          }
          return {
            id: page.id,
            name: page.name,
            image: page.picture_url,
            channels,
            authenticated: Boolean(page?.remote_token_status?.is_valid),
            account: page,
          } as SocialAccount
        })}
      />

      <FacebookPermissionsDialog
        initialFacebookPageId={currentSocialAccountId}
        open={isFacebookPermissionsDialogOpen}
        handleClose={() => {
          setIsFacebookPermissionsDialogOpen(false)
          setCurrentSocialAccountId(null)
          refetch()
        }}
      />
    </>
  )
}
const TwitterSocialAccountTable: React.FC<{}> = () => {
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const currentUser = useSelector(userSelector)
  const { enqueueSnackbar } = useSnackbar()
  const { twitterAccounts, loading, refetch } =
    useRetailerAccountSocialAccounts()
  const [disconnectTwitterAccount] = useMutation(DISCONNECT_TWITTER_ACCOUNT)
  const handleAddTwitterAccount = () => {
    const url = createTwitterAuthUrl({
      authOriginUrl: window.location.href,
      userId: currentUser.id,
      retailerAccountId: retailerAccount.id,
    })
    window.location.href = url
  }

  return (
    <SocialAccountTable
      onAdd={handleAddTwitterAccount}
      onEdit={(_) => handleAddTwitterAccount}
      onRemove={(account) => {
        disconnectTwitterAccount({
          variables: {
            id: account.id,
            resourceId: retailerAccount.id,
            resourceType: 'retailer_account',
          },
        })
          .then(({ data }) => {
            enqueueSnackbar(
              I18n.t(
                'retailerSettings.socialAccounts.confirmationDialog.success',
              ),
              {
                variant: 'success',
              },
            )
            refetch()
          })
          .catch((error) => {
            enqueueSnackbar(
              I18n.t(
                'retailerSettings.socialAccounts.confirmationDialog.error',
              ),
              {
                variant: 'error',
              },
            )
          })
      }}
      content={{
        title: I18n.t('retailerSettings.socialAccounts.twitterAccounts'),
        removeAccountContent: I18n.t(
          'retailerSettings.socialAccounts.confirmationDialog.description',
        ),
        removeAccountTitle: I18n.t(
          'retailerSettings.socialAccounts.confirmationDialog.title',
        ),
      }}
      loading={loading}
      rows={twitterAccounts.map((account) => {
        return {
          id: account.id,
          name: account.name,
          image: account.profile_image_url,
          channels: ['twitter'],
          authenticated: account.authenticated_local,
          account,
        } as SocialAccount
      })}
    />
  )
}
const SkeletonRow: React.FC<{}> = () => (
  <TableRow>
    <TableCell align="left">
      <Stack direction="row" spacing={2} alignContent={'center'}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
      </Stack>
    </TableCell>
    <TableCell align="center">
      <Stack direction="row" spacing={2} alignContent={'center'}>
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="circular" width={20} height={20} />
      </Stack>
    </TableCell>
    <TableCell align="center">
      <Skeleton variant="text" />
    </TableCell>
    <TableCell align="right">
      <Skeleton variant="text" />
    </TableCell>
  </TableRow>
)

const SocialAccountTable: React.FC<{
  content: {
    title: string
    removeAccountTitle: string
    removeAccountContent: string
  }
  loading: boolean
  rows: SocialAccount[]
  onAdd: () => void
  onRemove: (account: SocialAccount) => void
  onEdit: (account: SocialAccount) => void
}> = ({ content, loading, rows, onAdd, onEdit, onRemove }) => {
  const [isOpen, setOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<SocialAccount | null>(null)
  return (
    <>
      <div>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignContent="center"
        >
          <Typography variant="h6" gutterBottom>
            {content.title}
          </Typography>

          <Button onClick={() => onAdd()} size="small" variant="contained">
            {'+ '}
            {I18n.t('retailerSettings.socialAccounts.addSocialAccount', {
              socialAccount: content.title,
            })}
          </Button>
        </Stack>
      </div>
      <TableContainer component={Paper}>
        <Table aria-label={content.title}>
          <TableHead>
            <TableRow>
              <TableCell align="left">
                {I18n.t(
                  'retailerSettings.socialAccounts.socialAccountsTable.headers.name',
                )}
              </TableCell>
              <TableCell align="left" sx={{ width: '100px' }}>
                {I18n.t(
                  'retailerSettings.socialAccounts.socialAccountsTable.headers.channels',
                )}
              </TableCell>
              <TableCell align="center" sx={{ width: '100px' }}>
                {I18n.t(
                  'retailerSettings.socialAccounts.socialAccountsTable.headers.status',
                )}
              </TableCell>
              <TableCell align="right" sx={{ width: '100px' }}>
                {I18n.t(
                  'retailerSettings.socialAccounts.socialAccountsTable.headers.actions',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [<SkeletonRow />, <SkeletonRow />, <SkeletonRow />]
              : rows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="left">
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Avatar alt={row.name} src={row.image} />
                        <Typography>{row.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" spacing={2}>
                        {row.channels.map((channel) => {
                          switch (channel) {
                            case 'facebook':
                              return (
                                <FacebookIcon
                                  color="facebook"
                                  key={`${row.id}-${channel}`}
                                />
                              )
                            case 'instagram-invalid':
                              return (
                                <Tooltip
                                  title={I18n.t(
                                    'retailerSettings.socialAccounts.socialAccountsTable.channelNotAvailable',
                                    {
                                      channel: 'Instagram',
                                    },
                                  )}
                                >
                                  <InstagramIcon
                                    color="disabled"
                                    key={`${row.id}-${channel}`}
                                  />
                                </Tooltip>
                              )
                            case 'instagram':
                              return (
                                <Tooltip
                                  title={I18n.t(
                                    'retailerSettings.socialAccounts.socialAccountsTable.channelAvailable',
                                    { channel: 'Instagram' },
                                  )}
                                >
                                  <InstagramIcon
                                    color="instagram"
                                    key={`${row.id}-${channel}`}
                                  />
                                </Tooltip>
                              )
                            case 'twitter':
                              return (
                                <XIcon
                                  sx={{ color: 'twitter' }}
                                  key={`${row.id}-${channel}`}
                                />
                              )
                            default:
                              return undefined
                          }
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {row.authenticated ? (
                        <Tooltip
                          title={I18n.t(
                            'retailerSettings.socialAccounts.socialAccountsTable.accountConnected',
                          )}
                        >
                          <CheckCircleIcon color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={I18n.t(
                            'retailerSettings.socialAccounts.socialAccountsTable.fixAccount',
                          )}
                        >
                          <IconButton
                            color="error"
                            onClick={() => {
                              onEdit(row)
                            }}
                            aria-label={I18n.t(
                              'retailerSettings.socialAccounts.socialAccountsTable.fixAccount',
                            )}
                          >
                            <WarningIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={I18n.t(
                          'retailerSettings.socialAccounts.socialAccountsTable.removeAccount',
                          { name: row.name },
                        )}
                      >
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedRow(row)
                            setOpen(true)
                          }}
                          aria-label={I18n.t(
                            'retailerSettings.socialAccounts.socialAccountsTable.removeAccount',
                            { name: row.name },
                          )}
                        >
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 5,
                    }}
                  >
                    <Typography>
                      {I18n.t(
                        'retailerSettings.socialAccounts.socialAccountsTable.empty',
                      )}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        content={content.removeAccountContent}
        onClose={() => {
          setSelectedRow(null)
          setOpen(false)
        }}
        onConfirmClick={() => {
          setOpen(false)
          if (!selectedRow) {
            return
          }
          onRemove(selectedRow)
        }}
        open={isOpen}
        title={content.removeAccountTitle}
      />
    </>
  )
}
